/**
 * Module-level toast store.
 *
 * Toasts are ephemeral "your request reached the server" feedback — a single
 * line of text, no actions but dismiss. This store is the single source of
 * truth, deliberately living OUTSIDE the React tree so it can be driven both
 * by the `toast` singleton (`toast/useToast.ts`, components) and by the
 * imperative helpers in `lib/toast.ts` (e.g. the TanStack Query cache handlers
 * in `query-client.ts`, which run in module scope where a React context is
 * unreachable).
 *
 * `ToastProvider` subscribes via `useSyncExternalStore` and renders the dock.
 */

export type ToastTone = 'good' | 'bad' | 'info' | 'brand' | 'warn';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
  /** Auto-dismiss delay in seconds. */
  dur: number;
}

export interface ToastInput {
  tone?: ToastTone;
  message: string;
  dur?: number;
}

/** At most this many pills are visible; a new one drops the oldest. */
const MAX_VISIBLE = 3;
const DEFAULT_TONE: ToastTone = 'good';
const DEFAULT_DUR = 5;

let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export const toastStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /**
   * Returns the current toast list. The reference is stable between mutations
   * so `useSyncExternalStore` doesn't see a new snapshot every render.
   */
  getSnapshot(): ToastItem[] {
    return toasts;
  },

  add(input: ToastInput): string {
    const id = crypto.randomUUID();
    const item: ToastItem = {
      id,
      tone: input.tone ?? DEFAULT_TONE,
      message: input.message,
      dur: input.dur ?? DEFAULT_DUR,
    };
    const next = [...toasts, item];
    // Cap at MAX_VISIBLE — drop the oldest immediately when a 4th arrives.
    toasts = next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    emit();
    return id;
  },

  remove(id: string): void {
    const next = toasts.filter((t) => t.id !== id);
    // No-op when the id is already gone — keep the snapshot reference stable.
    if (next.length === toasts.length) return;
    toasts = next;
    emit();
  },

  /** Test-only: clears all toasts. */
  reset(): void {
    if (toasts.length === 0) return;
    toasts = [];
    emit();
  },
};
